import { ApiRegion, config } from '@/config';

import {
  classifyFetchError,
  classifyHttpError,
  mergeAbortSignals,
  RegionalApiError,
  STAFF_REGION_TOKEN_TIMEOUT_MS,
  timeoutSignal,
} from './regionalApi';

/**
 * Canada session → US Bearer (FOOD-883).
 * Keep the US token off the shared axios client so a US 401 cannot clear Canada auth.
 */

type Cache = {
  canadaAccessToken: string;
  accessToken: string;
  expiresAtMs: number;
};

let cache: Cache | null = null;
let inflight: { canadaAccessToken: string; promise: Promise<string> } | null = null;
let generation = 0;

export const usStaffTokenQueryKey = ['staff-region-token', ApiRegion.US] as const;

export const parseStaffTokenExpiresIn = (expiresIn: unknown): number => {
  if (typeof expiresIn !== 'number' || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    throw new RegionalApiError({
      kind: 'http',
      message: 'staff-region-token missing a finite expires_in',
    });
  }
  return expiresIn;
};

/** Logout / Canada-session change: drop cache and abandon any in-flight mint. */
export const clearStaffRegionTokenCache = () => {
  cache = null;
  inflight = null;
  generation += 1;
};

/**
 * Drop a US Bearer only if it is still the cached one.
 * Concurrent 401s must not bump generation or clear inflight: a second clear would
 * discard the first retry's mint, and callers should join the shared replacement promise.
 */
export const invalidateStaffRegionAccessToken = (rejectedAccessToken: string) => {
  if (cache?.accessToken !== rejectedAccessToken) {
    return;
  }
  cache = null;
};

/** US Bearer for the logged-in Canada chef. Refreshes ~60s before expiry. */
export const getUsStaffAccessToken = async (
  canadaAccessToken: string,
  opts: { signal?: AbortSignal } = {},
): Promise<string> => {
  if (cache?.canadaAccessToken === canadaAccessToken && Date.now() < cache.expiresAtMs - 60_000) {
    return cache.accessToken;
  }
  if (inflight?.canadaAccessToken === canadaAccessToken) {
    return inflight.promise;
  }

  const gen = generation;
  const promise = (async () => {
    let response: Response;
    try {
      response = await fetch(`${config.API_URL}/api/auth/staff-region-token`, {
        method: 'POST',
        signal: mergeAbortSignals([opts.signal, timeoutSignal(STAFF_REGION_TOKEN_TIMEOUT_MS)]),
        headers: {
          Authorization: `Bearer ${canadaAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ region: ApiRegion.US }),
      });
    } catch (error) {
      throw classifyFetchError(error);
    }
    if (response.status !== 200) {
      // This request mints against the Canada host with the Canada Bearer: a 401 here is
      // Canada rejecting Canada, not a US API failure (FOOD-1174).
      throw classifyHttpError(response, { mint: true });
    }

    const { access_token, expires_in } = await response.json();
    if (gen !== generation) {
      throw new Error('staff-region-token discarded after logout');
    }

    const expiresIn = parseStaffTokenExpiresIn(expires_in);
    cache = {
      canadaAccessToken,
      accessToken: access_token,
      expiresAtMs: Date.now() + expiresIn * 1000,
    };
    return access_token;
  })();

  inflight = { canadaAccessToken, promise };
  try {
    return await promise;
  } finally {
    if (inflight?.promise === promise) {
      inflight = null;
    }
  }
};
